import { ReactNode } from 'react'

import { cn } from '@/lib/misc/Utils'

import ArrowLeftIcon from '@icons/arrow-left'
import CheckIcon from '@icons/check'
import InfoIcon from '@icons/information-circle'

export interface OnboardingStep {
  key: string
  label: string
}

export const CREATE_STEPS: OnboardingStep[] = [
  { key: 'COMPANY', label: 'Your company' },
  { key: 'DETAILS', label: 'Company details' },
  { key: 'NETWORK', label: 'Network details' },
]

export const JOIN_STEPS: OnboardingStep[] = [
  { key: 'COMPANY', label: 'Your company' },
  { key: 'REQUEST', label: 'Request access' },
]

const StepIndicator = ({
  steps,
  activeStep,
}: {
  steps: OnboardingStep[]
  activeStep: string
}) => {
  const activeIndex = steps.findIndex(({ key }) => key === activeStep)

  return (
    <ol
      className="flex items-center gap-2 text-xs whitespace-nowrap"
      data-cy="onboarding-steps"
    >
      {steps.map(({ key, label }, index) => {
        const isDone = index < activeIndex
        const isActive = index === activeIndex

        return (
          <li key={key} className="flex items-center gap-2">
            {index > 0 && (
              <span
                className={cn(
                  'h-px w-4 sm:w-6',
                  isDone || isActive ? 'bg-accent-60' : 'bg-hairline'
                )}
              />
            )}
            <span
              className={cn(
                'flex items-center gap-1.5',
                isActive ? 'text-neutral-90' : 'text-neutral-50'
              )}
              {...(isActive && { 'aria-current': 'step' })}
            >
              <span
                className={cn(
                  'flex size-5 items-center justify-center rounded-full text-2xs font-semibold',
                  isDone && 'bg-accent-60 text-white',
                  isActive && 'bg-accent-10 text-accent-60',
                  !isDone && !isActive && 'bg-neutral-10 text-neutral-50'
                )}
              >
                {isDone ? <CheckIcon className="size-3" /> : index + 1}
              </span>
              <span className={cn(!isActive && 'hidden sm:inline')}>
                {label}
              </span>
            </span>
          </li>
        )
      })}
    </ol>
  )
}

const OnboardingPanel = ({
  icon,
  title,
  description,
  steps,
  activeStep,
  onBack,
  backLabel = 'Back',
  children,
}: {
  icon: ReactNode
  title: string
  description: ReactNode
  steps?: OnboardingStep[]
  activeStep?: string
  onBack?: () => void
  backLabel?: string
  children: ReactNode
}) => (
  <div className="mx-auto w-full max-w-2xl">
    {(onBack || steps) && (
      <div className="mb-4 flex flex-col items-start gap-3 sm:min-h-8 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        {onBack ? (
          <button
            type="button"
            onClick={onBack}
            className="flex items-center gap-1.5 whitespace-nowrap text-xs text-neutral-50 transition-colors hover:text-neutral-90"
            data-cy="onboarding-back-btn"
          >
            <ArrowLeftIcon className="size-4" />
            {backLabel}
          </button>
        ) : (
          <span className="hidden sm:block" />
        )}
        {steps && activeStep && (
          <StepIndicator steps={steps} activeStep={activeStep} />
        )}
      </div>
    )}

    <div className="rounded-2xl bg-surface px-7 py-8 shadow-card sm:px-9 sm:py-10">
      <div className="flex size-12 items-center justify-center rounded-xl bg-accent-10 text-accent-60">
        {icon}
      </div>
      <h1 className="mt-5 text-heading-3 tracking-tight text-neutral-90">
        {title}
      </h1>
      <p className="mt-2 text-sm leading-[1.5] text-neutral-50">
        {description}
      </p>
      <div className="mt-7">{children}</div>
    </div>
  </div>
)

export const CompanyCard = ({
  name,
  note,
  tone = 'neutral',
  trailing,
  className,
}: {
  name: string
  note?: ReactNode
  tone?: 'neutral' | 'accent'
  trailing?: ReactNode
  className?: string
}) => (
  <div
    className={cn(
      'flex items-center gap-4 rounded-xl border p-4',
      tone === 'accent'
        ? 'border-accent-20 bg-accent-05'
        : 'border-hairline bg-neutral-10/50',
      className
    )}
  >
    <div className="flex size-11 shrink-0 items-center justify-center rounded-lg bg-accent-20 text-lg font-semibold uppercase text-accent-60">
      {name.slice(0, 1)}
    </div>
    <div className="min-w-0 flex-1">
      <div className="truncate font-medium text-neutral-90">{name}</div>
      {note != null && <div className="text-xs text-neutral-50">{note}</div>}
    </div>
    {trailing}
  </div>
)

export const StatusBadge = ({
  tone,
  icon,
  children,
}: {
  tone: 'good' | 'warning'
  icon?: ReactNode
  children: ReactNode
}) => (
  <span
    className={cn(
      'inline-flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-2xs font-medium',
      tone === 'good'
        ? 'bg-status-good/10 text-[#217a4f]'
        : 'bg-status-warning/15 text-status-warning-900'
    )}
  >
    {icon}
    {children}
  </span>
)

export const PanelNote = ({ children }: { children: ReactNode }) => (
  <div className="flex gap-2.5 rounded-xl bg-neutral-10 px-3.5 py-3 text-xs leading-[1.5] text-neutral-70">
    <InfoIcon className="mt-px size-4 shrink-0 text-accent-60" />
    <div>{children}</div>
  </div>
)

export const PanelDivider = ({ label }: { label: string }) => (
  <div className="my-6 flex items-center gap-3 text-2xs text-neutral-50">
    <span className="h-px flex-1 bg-hairline" />
    {label}
    <span className="h-px flex-1 bg-hairline" />
  </div>
)

export const Timeline = ({
  items,
}: {
  items: { title: string; detail: ReactNode; done?: boolean }[]
}) => (
  <ol>
    {items.map(({ title, detail, done }, index) => (
      <li key={title} className="flex gap-3">
        <div className="flex w-[22px] shrink-0 flex-col items-center">
          <span
            className={cn(
              'mt-[5px] size-2.5 rounded-full',
              done ? 'bg-accent-60' : 'bg-neutral-30'
            )}
          />
          {index < items.length - 1 && (
            <span className="my-1 w-px flex-1 bg-hairline" />
          )}
        </div>
        <div className="pb-4.5">
          <div
            className={cn(
              'text-xs',
              done ? 'font-medium text-neutral-90' : 'text-neutral-50'
            )}
          >
            {title}
          </div>
          <div className="mt-0.5 text-xs leading-[1.45] text-neutral-50">
            {detail}
          </div>
        </div>
      </li>
    ))}
  </ol>
)

export const PanelFooter = ({
  leading,
  children,
}: {
  leading?: ReactNode
  children: ReactNode
}) => (
  <div className="mt-7 flex items-center justify-between gap-3 border-t border-hairline pt-5">
    {leading ?? <span />}
    <div className="flex items-center gap-3">{children}</div>
  </div>
)

export default OnboardingPanel
